<template>
  <div class="pc-cart">
    <section class="pc-container cart-wrap">
      <div v-if="!items.length" class="empty">
        <h2>购物车暂无商品</h2>
        <button @click="$router.push('/pc/products')">去选购商品</button>
      </div>

      <template v-else>
        <div class="cart-table-head cart-columns">
          <label><input type="checkbox" :checked="allSelected" @change="toggleAll"> 全选</label>
          <span>商品型号</span>
          <span>商品信息</span>
          <span>单价（含税）</span>
          <span>数量</span>
          <span>金额（含税）</span>
          <span>操作</span>
        </div>

        <div class="stock-group">
          <article v-for="item in items" :key="item.id" class="cart-row cart-columns" :class="{ invalid: item.invalid }">
            <label class="row-check"><input type="checkbox" :checked="item.checked" :disabled="item.invalid" @change="toggleItem(item)"></label>
            <div class="model-cell">
              <img :src="item.image" :alt="productTitle(item)" @error="handleProductImageError">
              <div>
                <h2>{{ productTitle(item) }}</h2>
                <p v-if="productType(item)">产品类型：{{ productType(item) }}</p>
                <p>品牌：{{ productBrand(item) }}</p>
                <p>封装：{{ item.package }}</p>
                <p>包装数量：{{ item.packagingQuantity == null ? '--' : item.packagingQuantity }}</p>
                <p>批次：{{ item.batch || '--' }}</p>
                <router-link class="datasheet-link" :to="productFilesRoute(item)" target="_blank" rel="noopener">
                  <img :src="icons.file" alt="">数据手册
                </router-link>
              </div>
            </div>
            <div class="goods-info">
              <p>{{ item.desc }}</p>
              <p>库存：<b>{{ item.stock }}</b></p>
              <p>起订量：{{ item.min }}</p>
              <p v-if="item.invalid" class="invalid-tip">{{ item.invalidReason }}</p>
            </div>
            <div>{{ item.hasPrice ? '¥ ' + itemUnitPrice(item) : '暂无价格/联系询价' }}</div>
            <div class="quantity-cell">
              <div class="quantity-box">
                <button :disabled="item.invalid" @click="changeQuantity(item, -(item.increment || 1))"><img :src="icons.minus" alt="减少"></button>
                <input :value="item.quantity" :disabled="item.invalid" @input="handleQuantityInput(item, $event)">
                <button :disabled="item.invalid" @click="changeQuantity(item, item.increment || 1)"><img :src="icons.plus" alt="增加"></button>
              </div>
              <small>库存 {{ item.stock }}</small>
            </div>
            <strong v-if="item.hasPrice" class="amount">¥ {{ itemAmount(item) }}</strong>
            <strong v-else class="amount">联系询价</strong>
            <button class="remove" @click="removeItem(item)">删除</button>
          </article>
        </div>

        <div class="cart-footer">
          <div class="footer-tools">
            <label><input type="checkbox" :checked="allSelected" @change="toggleAll"> 全选</label>
            <span>已选中 <b>{{ checkedItems.length }}</b> 种商品</span>
            <button @click="removeSelected">删除</button>
            <button @click="exportSelected">导出清单</button>
          </div>
          <div class="footer-summary">
            <span>应付金额：</span>
            <strong>¥ {{ totalAmount }}</strong>
            <button :disabled="!checkedItems.length" @click="goCheckout">去结算</button>
          </div>
        </div>
      </template>
    </section>
  </div>
</template>

<script>
  import { message } from 'ant-design-vue'
  import commerce from '@/utils/pcCommerce'
  import { productTitle, productType, productBrand } from '@/utils/pcProductDisplay'
  import { buildPcDetailPath } from '@/utils/pcRouteCode'
  import pcAuth from '@/utils/pcAuth'
  import { checkPcCartProducts, getPcMallSettings } from '@/api/pc'
  import { getCartItems, removeCartItem, saveCartItems, updateCartItem } from '@/utils/pcCart'

  const pcIcons = {
    plus: require('@/assets/icons/pc/plus.svg'),
    minus: require('@/assets/icons/pc/minus.svg'),
    file: require('@/assets/icons/pc/invoice.svg')
  }

  export default {
    name: 'PcCart',
    data() {
      return { items: [], icons: pcIcons, companyName: '锐高科技' }
    },
    computed: {
      checkedItems() {
        return this.items.filter(item => item.checked && !item.invalid)
      },
      allSelected() {
        const validItems = this.items.filter(item => !item.invalid)
        return validItems.length > 0 && validItems.every(item => item.checked)
      },
      totalAmount() {
        const total = this.checkedItems.reduce((sum, item) => sum + Number(this.itemAmount(item)), 0)
        return commerce.formatMoney(total, 2)
      }
    },
    created() {
      this.items = getCartItems()
      this.refreshCartProducts()
      getPcMallSettings().then(data => { this.companyName = (data && data.companyName) || this.companyName }).catch(() => {})
    },
    methods: {
      productTitle,
      productType,
      productBrand,
      productFilesRoute(item) {
        return {
          path: buildPcDetailPath('product', item && (item.productId || item.id)),
          query: { tab: 'files' },
          hash: '#product-files'
        }
      },
      toggleAll() {
        const checked = !this.allSelected
        this.items = this.items.map(item => ({ ...item, checked: item.invalid ? false : checked }))
        saveCartItems(this.items)
        this.$emit('cart-change')
      },
      toggleItem(item) {
        if (item.invalid) return
        this.items = updateCartItem(item.id, { checked: !item.checked })
        this.$emit('cart-change')
      },
      changeQuantity(item, delta) {
        if (item.invalid) return
        const quantity = Math.min(Number(item.stock || 0), Math.max(item.min || 1, Number(item.quantity || item.min || 1) + Number(delta || 1)))
        this.items = updateCartItem(item.id, { quantity })
        this.$emit('cart-change')
      },
      handleQuantityInput(item, event) {
        if (item.invalid) return
        const quantity = Math.min(Number(item.stock || 0), Math.max(item.min || 1, Number(event.target.value || item.min || 1)))
        this.items = updateCartItem(item.id, { quantity })
        this.$emit('cart-change')
      },
      removeItem(item) {
        this.items = removeCartItem(item.id)
        this.$emit('cart-change')
      },
      removeSelected() {
        if (!this.checkedItems.length) return message.warning('请先选择需要删除的商品')
        const selectedIds = this.checkedItems.map(item => String(item.id))
        this.items = this.items.filter(item => selectedIds.indexOf(String(item.id)) === -1)
        saveCartItems(this.items)
        this.$emit('cart-change')
      },
      itemUnitPrice(item) {
        return commerce.formatMoney(commerce.getTierPrice(item, item.quantity), 4)
      },
      itemAmount(item) {
        return commerce.formatMoney(commerce.getTierPrice(item, item.quantity) * Number(item.quantity || 0), 2)
      },
      async refreshCartProducts() {
        if (!this.items.length) return
        try {
          const products = await checkPcCartProducts(this.items.map(item => item.productId || item.id))
          const productMap = new Map((products || []).map(item => [String(item.id), commerce.normalizeProduct(item)]))
          this.items = this.items.map(item => {
            const product = productMap.get(String(item.productId || item.id))
            if (!product) return { ...item, checked: false, invalid: true, invalidReason: '商品已删除或已失效' }
            const offShelf = Number(product.status) !== 1
            const insufficient = Number(product.stock || 0) < Number(product.min || 1)
            const noPrice = !product.hasPrice
            const invalid = offShelf || insufficient || noPrice
            return {
              ...item,
              ...product,
              quantity: invalid ? item.quantity : Math.min(Number(product.stock), Math.max(Number(product.min), Number(item.quantity || product.min))),
              checked: invalid ? false : item.checked,
              invalid,
              invalidReason: offShelf ? '商品已下架' : (insufficient ? '库存不足，暂时无法结算' : (noPrice ? '暂无价格，请联系询价' : ''))
            }
          })
          saveCartItems(this.items)
          this.$emit('cart-change')
        } catch (error) {
          message.warning(error.message || '购物车库存刷新失败，请稍后重试')
        }
      },
      handleProductImageError(event) {
        commerce.handleProductImageError(event)
      },
      async exportSelected() {
        if (!this.checkedItems.length) return message.warning('请先选择需要导出的商品')
        try {
          const excelModule = await import('exceljs')
          const ExcelJS = excelModule.default || excelModule
          const workbook = new ExcelJS.Workbook()
          const worksheet = workbook.addWorksheet('Sheet1')
          worksheet.addTable({
            name: 'CartDetail', ref: 'A1', headerRow: true,
            style: { theme: 'TableStyleLight1', showRowStripes: false },
            columns: ['商品型号', '库存类型', '商品分类', '品牌', '封装', '数量', '单价（含税）', '金额（含税）']
              .map(name => ({ name })),
            rows: this.checkedItems.map(item => [
              productTitle(item), '现货', item.categoryName || item.subCategoryName || item.category || item.desc || '',
              productBrand(item), item.package, Number(item.quantity || 0),
              Number(this.itemUnitPrice(item)), Number(this.itemAmount(item))
            ])
          })
          const widths = [32, 14, 25, 24, 20, 14, 16, 14]
          widths.forEach((width, index) => { worksheet.getColumn(index + 1).width = width })
          const lastRow = Math.max(2, worksheet.rowCount)
          worksheet.getRow(1).font = { bold: false, size: 11 }
          worksheet.getRow(1).height = 22
          worksheet.getColumn(6).numFmt = '0'
          worksheet.getColumn(7).numFmt = '0.0000'
          worksheet.getColumn(8).numFmt = '0.00'
          for (let row = 1; row <= lastRow; row += 1) worksheet.getRow(row).alignment = { vertical: 'middle' }
          const buffer = await workbook.xlsx.writeBuffer()
          const url = URL.createObjectURL(new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' }))
          const link = document.createElement('a')
          link.href = url
          link.download = `${this.companyName}在线商城购物车详情.xlsx`
          link.click()
          URL.revokeObjectURL(url)
        } catch (error) {
          message.error(error.message || '购物车清单导出失败')
        }
      },
      goCheckout() {
        if (!this.checkedItems.length) return message.warning('请先选择需要结算的商品')
        if (!pcAuth.getToken()) {
          message.info('请先登录后再结算')
          this.$router.push({ path: '/pc/login', query: { redirect: '/pc/checkout' } })
          return
        }
        this.$router.push('/pc/checkout')
      }
    }
  }
</script>

<style scoped>
  .pc-cart { min-height: calc(100vh - 176px); padding: 16px 0 60px; background: #f3f5f9; }
  .cart-wrap { min-height: 500px; }
  .cart-columns { display: grid; grid-template-columns: 70px minmax(300px, 1.6fr) minmax(210px, 1.2fr) 130px 150px 130px 80px; align-items: center; gap: 12px; }
  .cart-table-head { min-height: 48px; padding: 0 16px; border-radius: 10px; background: #fff; color: #1f2937; font-weight: 700; }
  input[type='checkbox'] { accent-color: #1684ff; }
  .stock-group { margin-top: 12px; border-radius: 10px; overflow: hidden; background: #fff; }
  .cart-row { min-height: 136px; padding: 16px; border-bottom: 1px solid #eef1f5; color: #475569; }
  .cart-row:last-child { border-bottom: 0; }
  .cart-row.invalid { background: #fafafa; opacity: .72; }
  .invalid-tip { color: #f5222d; font-weight: 700; }
  .row-check { text-align: left; }
  .model-cell { display: grid; grid-template-columns: 96px minmax(0, 1fr); gap: 12px; align-items: center; min-width: 0; }
  .model-cell > img { width: 96px; height: 96px; object-fit: contain; border-radius: 5px; background: #f7f8fa; }
  .model-cell h2 { margin: 0 0 8px; color: #111827; font-size: 16px; word-break: break-all; }
  .model-cell p, .goods-info p { margin: 5px 0; font-size: 13px; }
  .datasheet-link { width: fit-content; height: 18px; margin: 5px 0; padding: 0 8px; border: 1px solid #e5e7eb; border-radius: 9px; box-sizing: border-box; display: inline-flex; align-items: center; gap: 6px; background: #f8fafc; color: #334155; font-size: 13px; line-height: 1.2; text-decoration: none; }
  .datasheet-link:hover { border-color: #1684ff; color: #1684ff; }
  .datasheet-link img { width: 13px; height: 13px; }
  .goods-info b { color: #1684ff; }
  .quantity-box { width: 126px; height: 32px; display: grid; grid-template-columns: 32px 1fr 32px; border: 1px solid #d5dbe5; border-radius: 6px; overflow: hidden; }
  .quantity-box button, .quantity-box input { min-width: 0; border: 0; border-right: 1px solid #d5dbe5; background: #fff; text-align: center; outline: 0; }
  .quantity-box button:last-child { border-right: 0; }
  .quantity-box button { display: flex; align-items: center; justify-content: center; }
  .quantity-box img { width: 13px; height: 13px; }
  .quantity-cell { position: relative; height: 32px; display: flex; align-items: center; }
  .quantity-cell small { position: absolute; top: 45px; left: 0; margin: 0; color: #64748b; white-space: nowrap; }
  .amount { color: #ff4d00; }
  .remove { justify-self: center; text-align: center; }
  .remove, .footer-tools button { border: 0; background: transparent; color: #1684ff; cursor: pointer; }
  .cart-footer { min-height: 104px; margin-top: 12px; padding: 18px 20px; border-radius: 10px; display: flex; align-items: center; justify-content: space-between; background: #fff; box-sizing: border-box; }
  .footer-tools { display: flex; align-items: center; gap: 22px; }
  .footer-tools b { color: #ff4d00; }
  .footer-summary { display: flex; align-items: center; gap: 12px; }
  .footer-summary strong { margin-right: 10px; color: #ff4d00; font-size: 25px; }
  .footer-summary button, .empty button { width: 180px; height: 48px; border: 0; border-radius: 24px; background: #1684ff; color: #fff; font-size: 18px; font-weight: 700; cursor: pointer; }
  .footer-summary button:disabled { background: #aeb8c5; cursor: not-allowed; }
  .empty { padding: 150px 0; border-radius: 10px; background: #fff; text-align: center; color: #64748b; }
  .empty button { width: 140px; height: 40px; margin-top: 15px; font-size: 15px; }
</style>
